import { EXPORT_ROW_CAP } from "./csv-export";
import { Field, fieldLabel, isReadOnlyField } from "./schema-field";

const SKIP_HEADERS = new Set(["actions", "__check__"]);

export type ParsedCsvImport = {
  rows: Record<string, string>[];
  truncated: boolean;
};

/** Split export-shaped CSV (quoted commas/newlines, optional BOM) into records. */
export const parseCsvRecords = (text: string): string[][] => {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let i = 0;
  let inQuotes = false;

  while (i < src.length) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (c === "\n" || c === "\r") {
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
      if (c === "\r" && src[i + 1] === "\n") i += 2;
      else i += 1;
      continue;
    }
    field += c;
    i += 1;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

const skipColumn = (header: string, field?: Field): boolean => {
  const name = (field?.name ?? header).trim();
  if (SKIP_HEADERS.has(header) || SKIP_HEADERS.has(name)) return true;
  if (header.startsWith("_") || name.startsWith("_")) return true;
  if (field) return isReadOnlyField(field);
  return name.toLowerCase() === "id";
};

const fieldForHeader = (header: string, fields: Field[]): Field | undefined =>
  fields.find((field) => fieldLabel(field) === header) ??
  fields.find((field) => field.name === header);

/**
 * Map an exported table CSV onto `schema` columns. Headers match `fieldLabel`
 * first, then `field.name`. Data rows past `cap` are dropped.
 */
export const parseCsvImport = (
  text: string,
  fields: Field[],
  cap: number = EXPORT_ROW_CAP
): ParsedCsvImport => {
  const records = parseCsvRecords(text);
  if (records.length === 0) return { rows: [], truncated: false };

  const headers = records[0].map((header) => header.trim());
  const seen = new Set<string>();
  const columns = headers.map((header) => {
    if (!header) return null;
    const field = fieldForHeader(header, fields);
    if (skipColumn(header, field) || !field) return null;
    if (seen.has(field.name)) return null;
    seen.add(field.name);
    return field.name;
  });

  const dataRecords = records
    .slice(1)
    .filter((record) => record.some((cell) => cell !== ""));
  const truncated = dataRecords.length > cap;
  const slice = truncated ? dataRecords.slice(0, cap) : dataRecords;

  const rows = slice.map((record) => {
    const row: Record<string, string> = {};
    columns.forEach((name, index) => {
      if (!name) return;
      row[name] = record[index] ?? "";
    });
    return row;
  });

  return { rows, truncated };
};
